import { SwapRouter } from '@trinity/pharaoh-common';
import { Contract } from 'ethers';
export class SwapService {
    constructor(runner, routerAddress, tokenService) {
        this.router = new Contract(routerAddress, SwapRouter, runner);
        this.tokenService = tokenService;
    }
    /**
     * Returns the address of the router contract
     */
    getRouterAddress() {
        return this.router.target;
    }
    /**
     * Get a signer from runner properly handling BrowserProvider
     */
    async getSigner() {
        const runner = this.router.runner;
        if (!runner) {
            throw new Error('Contract runner is not available');
        }
        let signer;
        // First check if we have a BrowserProvider
        if ('getSigner' in runner) {
            try {
                // Runner is a BrowserProvider, get a signer
                signer = await runner.getSigner();
            }
            catch (e) {
                console.error('Error getting signer from BrowserProvider:', e);
                throw new Error('Could not get signer from BrowserProvider');
            }
        }
        else {
            // Assume it's a Signer or something else with similar capabilities
            signer = runner;
            console.log('Using runner directly as signer');
        }
        // Get the signer address safely
        let address;
        try {
            address = await signer.getAddress();
        }
        catch (e) {
            console.error('Error calling getAddress on signer:', e);
            throw new Error('Could not get address from signer');
        }
        return { signer, address };
    }
    /**
     * Prepare transaction options by converting gasLimit to bigint
     */
    prepareTransactionOptions(options) {
        return {
            gasLimit: options.gasLimit !== undefined ? BigInt(options.gasLimit) : 1000000n,
            gasPrice: options.gasPrice,
        };
    }
    /**
     * Execute token swap
     */
    async swap(tokenIn, tokenOut, amountIn, amountOutMinimum, options = {}) {
        // Determine if native token (ETH/AVAX) is involved
        const isNativeIn = tokenIn.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
            tokenIn.toLowerCase() === '0x0000000000000000000000000000000000000000';
        // Check if tokenOut is native ETH/AVAX
        const isNativeOut = tokenOut.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
            tokenOut.toLowerCase() === '0x0000000000000000000000000000000000000000';
        // Route to the appropriate swap method based on the token types
        switch (true) {
            case isNativeIn:
                return this.swapExactNativeToERC20(tokenOut, amountIn, amountOutMinimum, options);
            case isNativeOut:
                return this.swapExactERC20ToNative(tokenIn, amountIn, amountOutMinimum, options);
            default:
                return this.swapExactERC20ToERC20(tokenIn, tokenOut, amountIn, amountOutMinimum, options);
        }
    }
    /**
     * Swap an exact amount of ERC20 tokens for another ERC20 token
     */
    async swapExactERC20ToERC20(tokenIn, tokenOut, amountIn, amountOutMinimum, options = {}) {
        const { feeTier = 500, deadline = Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
        recipient = null, } = options;
        // Prepare ethers transaction options
        const txOptions = this.prepareTransactionOptions(options);
        // Get signer and address using our helper method
        const { signer, address: signerAddress } = await this.getSigner();
        // Make sure we have a router connected to the signer
        const routerWithSigner = new Contract(this.router.target, SwapRouter, signer);
        const actualRecipient = recipient || signerAddress;
        // Store initial balance for later comparison
        const initialBalance = await this.tokenService.getBalance(tokenOut, actualRecipient);
        // Approve the router to spend tokens
        const approveTokenTx = await this.tokenService.approve(tokenIn, this.router.target, amountIn, signer);
        // Create params for the swap
        const exactInputSingleParams = {
            tokenIn,
            tokenOut,
            fee: feeTier,
            recipient: actualRecipient,
            deadline,
            amountIn,
            amountOutMinimum,
            sqrtPriceLimitX96: 0,
            veNFTTokenId: 0, // Pharaoh specific param
        };
        const multicallData = [
            routerWithSigner.interface.encodeFunctionData('exactInputSingle', [exactInputSingleParams]),
        ];
        // Execute the swap with native token value and calculated gas limit
        const tx = await routerWithSigner.multicall(multicallData, txOptions);
        await approveTokenTx.wait();
        await tx.wait();
        // Get final balance to calculate amount out
        const finalBalance = await this.tokenService.getBalance(tokenOut, actualRecipient);
        const amountOut = BigInt(finalBalance.balance - initialBalance.balance);
        const outDecimals = await this.tokenService.getTokenDecimals(tokenOut);
        return {
            hash: tx.hash,
            amountOut,
            decimals: outDecimals,
        };
    }
    /**
     * Swap an exact amount of native tokens (ETH/AVAX) for ERC20 tokens
     */
    async swapExactNativeToERC20(tokenOut, amountIn, amountOutMinimum, options = {}) {
        const { feeTier = 500, deadline = Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
        recipient = null, } = options;
        // Prepare ethers transaction options
        const txOptions = this.prepareTransactionOptions(options);
        // Get signer and address using our helper method
        const { signer, address: signerAddress } = await this.getSigner();
        // Make sure we have a router connected to the signer
        const routerWithSigner = new Contract(this.router.target, SwapRouter, signer);
        const actualRecipient = recipient || signerAddress;
        // Store initial token balance for later comparison
        const tokenOutContract = new Contract(tokenOut, ['function balanceOf(address) view returns (uint256)'], signer);
        const initialBalance = await tokenOutContract.balanceOf(actualRecipient);
        // For native token swaps, we need to use the wrapped version for the contract call
        // Get the wrapped native token address (WETH/WAVAX)
        const wrappedNativeAddress = this.tokenService.getNativeWrappedTokenAddresses()[0];
        // Create params for the swap
        const exactInputSingleParams = {
            tokenIn: wrappedNativeAddress, // Use wrapped native token address
            tokenOut,
            fee: feeTier,
            recipient: actualRecipient,
            deadline,
            amountIn,
            amountOutMinimum,
            sqrtPriceLimitX96: 0,
            veNFTTokenId: 0, // Pharaoh specific param
        };
        // Build multicall data for router
        const multicallData = [
            routerWithSigner.interface.encodeFunctionData('exactInputSingle', [exactInputSingleParams]),
        ];
        // Execute the swap with native token value and calculated gas limit and wait for it
        const tx = await routerWithSigner.multicall(multicallData, {
            ...txOptions,
            value: amountIn,
        });
        await tx.wait();
        // Get final balance to calculate amount out
        const finalBalance = await tokenOutContract.balanceOf(actualRecipient);
        const amountOut = BigInt(finalBalance - initialBalance);
        const outDecimals = await this.tokenService.getTokenDecimals(tokenOut);
        return {
            hash: tx.hash,
            amountOut,
            decimals: outDecimals,
        };
    }
    /**
     * Swap an exact amount of ERC20 tokens for native tokens (ETH/AVAX)
     */
    async swapExactERC20ToNative(tokenIn, amountIn, amountOutMinimum, options = {}) {
        const { feeTier = 500, deadline = Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
        recipient = null, } = options;
        // Prepare ethers transaction options with doubled gas limit for native swaps
        const baseTxOptions = this.prepareTransactionOptions(options);
        const txOptions = {
            ...baseTxOptions,
            gasLimit: baseTxOptions.gasLimit ? baseTxOptions.gasLimit * 2n : 2000000n,
        };
        // Get signer and address using our helper method
        const { signer, address: signerAddress } = await this.getSigner();
        // Make sure we have a router connected to the signer
        const routerWithSigner = new Contract(this.router.target, SwapRouter, signer);
        const actualRecipient = recipient || signerAddress;
        // Store initial native balance for later comparison
        const initialBalance = (await signer.provider?.getBalance(actualRecipient)) || 0n;
        // Approve the router to spend tokens
        const approveTokenTx = await this.tokenService.approve(tokenIn, this.router.target, amountIn, signer);
        // For token to native swaps, we need to:
        // 1. First swap the token to wrapped native token (WETH/WAVAX)
        // 2. Then unwrap the WETH/WAVAX to native (ETH/AVAX)
        // For native token swaps, we need to use the wrapped version for the contract call
        // Get the wrapped native token address (WETH/WAVAX)
        const wrappedNativeAddress = this.tokenService.getNativeWrappedTokenAddresses()[0];
        // Create params for the swap
        const exactInputSingleParams = {
            tokenIn,
            tokenOut: wrappedNativeAddress, // This is the wrapped native token address
            fee: feeTier,
            recipient: routerWithSigner.target, // First send to the router
            deadline,
            amountIn,
            amountOutMinimum,
            sqrtPriceLimitX96: 0,
            veNFTTokenId: 0, // Pharaoh specific param
        };
        // Build multicall data for router
        const multicallData = [
            routerWithSigner.interface.encodeFunctionData('exactInputSingle', [exactInputSingleParams]),
            // Unwrap the WETH/WAVAX to native ETH/AVAX and send to the recipient
            routerWithSigner.interface.encodeFunctionData('unwrapWETH9', [
                amountOutMinimum, // Amount to unwrap
                actualRecipient, // Address to send the unwrapped ETH/AVAX to
            ]),
        ];
        // Execute the swap with doubled gas limit and wait for it
        const tx = await routerWithSigner.multicall(multicallData, txOptions);
        await approveTokenTx.wait();
        await tx.wait();
        // Get final balance
        const finalBalance = (await signer.provider?.getBalance(actualRecipient)) || 0n;
        // For native output, the transaction costs affect our balance calculation
        // Try to extract the amount from the transaction logs
        const amountOut = finalBalance - initialBalance;
        const outDecimals = await this.tokenService.getTokenDecimals(wrappedNativeAddress);
        return {
            hash: tx.hash,
            amountOut,
            decimals: outDecimals,
        };
    }
}
